import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Loader2, TestTube } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useNoIndex } from '../hooks/useNoIndex';

const PaymentCallbackPage = () => {
  useNoIndex();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Ödeme işleniyor...');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isTestPayment, setIsTestPayment] = useState(false);

  useEffect(() => {
    const processCallback = () => {
      // Get parameters from URL (set by backend redirect)
      const urlStatus = searchParams.get('status');
      const urlMessage = searchParams.get('message');
      const paymentId = searchParams.get('paymentId');
      const errorCode = searchParams.get('errorCode');
      
      console.log('[PaymentCallback] URL params:', { urlStatus, urlMessage, paymentId, errorCode });
      
      if (urlStatus === 'success') {
        setStatus('success');
        setMessage('Ödemeniz başarıyla tamamlandı! PRO üyeliğiniz aktif edildi.');
        setPaymentDetails({ paymentId });
        setIsTestPayment(false);
      } else if (urlStatus === 'test_success') {
        setStatus('success');
        setMessage('Test ödemeniz başarıyla tamamlandı! (PRO aktif edilmedi - test modu)');
        setPaymentDetails({ paymentId });
        setIsTestPayment(true);
      } else if (urlStatus === 'error') {
        setStatus('error');
        setMessage(decodeURIComponent(urlMessage || 'Ödeme işlemi başarısız oldu.').replace(/\+/g, ' '));
        if (errorCode) {
          setPaymentDetails({ errorCode });
        }
      } else {
        // No status in URL - still processing or direct access
        setStatus('error');
        setMessage('Ödeme durumu belirlenemedi. Lütfen ayarlar sayfasından durumu kontrol edin.');
      }
    };

    // Small delay to show processing state
    setTimeout(processCallback, 500);
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {status === 'processing' && (
              <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
            )}
            {status === 'success' && !isTestPayment && (
              <CheckCircle className="w-16 h-16 text-green-500" />
            )}
            {status === 'success' && isTestPayment && (
              <div className="relative">
                <CheckCircle className="w-16 h-16 text-amber-500" />
                <TestTube className="w-6 h-6 text-amber-600 absolute -bottom-1 -right-1 bg-background rounded-full p-0.5" />
              </div>
            )}
            {status === 'error' && (
              <XCircle className="w-16 h-16 text-red-500" />
            )}
          </div>
          <CardTitle className="text-xl">
            {status === 'processing' && 'İşleniyor...'}
            {status === 'success' && !isTestPayment && 'Ödeme Başarılı!'}
            {status === 'success' && isTestPayment && 'Test Ödemesi Başarılı!'}
            {status === 'error' && 'Ödeme Başarısız'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">{message}</p>
          
          {status === 'success' && paymentDetails?.paymentId && (
            <div className={`rounded-lg p-4 text-sm ${isTestPayment ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-muted/50'}`}>
              <p><strong>Ödeme ID:</strong> {paymentDetails.paymentId}</p>
              {isTestPayment && (
                <p className="mt-2 text-xs text-amber-600">
                  Bu bir test ödemesidir. İade için bu ID'yi kullanabilirsiniz.
                </p>
              )}
            </div>
          )}
          
          {status === 'error' && paymentDetails?.errorCode && (
            <div className="bg-red-500/10 rounded-lg p-4 text-sm">
              <p><strong>Hata Kodu:</strong> {paymentDetails.errorCode}</p>
            </div>
          )}
          
          <div className="pt-4">
            {status === 'success' && !isTestPayment && (
              <Button 
                onClick={() => navigate('/dashboard')} 
                className="w-full"
                data-testid="goto-dashboard-btn"
              >
                Dashboard'a Git
              </Button>
            )}
            {status === 'success' && isTestPayment && (
              <Button 
                onClick={() => navigate('/dashboard/settings')} 
                className="w-full"
                variant="outline"
                data-testid="goto-settings-btn"
              >
                Ayarlara Dön
              </Button>
            )}
            {status === 'error' && (
              <div className="space-y-2">
                <Button 
                  onClick={() => navigate('/pricing')} 
                  className="w-full"
                  data-testid="retry-btn"
                >
                  Tekrar Dene
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/contact')} 
                  className="w-full"
                  data-testid="support-btn"
                >
                  Destek Al
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentCallbackPage;
